import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';

import { Status, SubType, Vehicle, VehicleBody } from '@quorum/models/xs-resource';
import { VehicleQueryParameters } from '@quorum/models/xs-query';
import { ApiService } from '@quorum/api';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
@Component({
  selector: 'sha-create-vehicle',
  templateUrl: './create-vehicle.component.html',
  styleUrls: ['./create-vehicle.component.scss']
})
export class CreateVehicleComponent implements OnInit, OnChanges, OnDestroy {
  @Input() vehicle: Vehicle;
  @Input() vehicleDropdowns$: Observable<any>;
  @Output() vehicleEmitted: EventEmitter<{
    vehicle: Vehicle;
  }> = new EventEmitter<{ vehicle: Vehicle }>();
  @Output() formValidity: EventEmitter<boolean> = new EventEmitter<boolean>();
  vehicleForm: FormGroup;
  trims: any[] = [];
  createReady: boolean = false;
  dateMask = createAutoCorrectedDatePipe('mm/dd/yyyy');
  minInServiceDate: Date = new Date();
  maxInServiceDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));

  vehicleFormVinSub: Subscription;
  vehicleFormTrimLevelSub: Subscription;
  addressFormProvStateSub: Subscription;
  vinError: string = '';
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  bodyDropdowns$: Observable<VehicleBody[]>;
  vehicleFormChangesSub: Subscription;

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.vehicleFormChangesSub = this.vehicleForm.valueChanges.subscribe(value => {
      if (value.vin != null && value.trimLevel != null) {
        this.formValidity.emit(this.vehicleForm.valid);
      }

      if (this.vehicleForm.valid && value.vin != null && value.trimLevel != null && value.prospectColourId != null) {
        console.log(`emitted vehicle `);
        this.vehicleEmitted.emit({ vehicle: this.vehicleForm.getRawValue() });
      }
    });

    this.vehicleFormVinSub = this.vehicleForm['controls'].vin.valueChanges
      .pipe(
        filter((input: string) => input.length >= 10),
        debounceTime(500),
        distinctUntilChanged(),
        tap(vin => {
          this.vinError = '';
          if (vin.length < 17) {
            this.vinError = 'vinLengthFailed';
          } else {
            let vinTotal: number = 0;
            let digitValue: number;
            let checkDigit: any;
            for (var i = 0; i < vin.length; i++) {
              if (i !== 8) {
                switch (vin.charAt(i)) {
                  case 'A':
                  case 'J':
                    digitValue = 1;
                    break;
                  case 'B':
                  case 'K':
                  case 'S':
                    digitValue = 2;
                    break;
                  case 'C':
                  case 'L':
                  case 'T':
                    digitValue = 3;
                    break;
                  case 'D':
                  case 'M':
                  case 'U':
                    digitValue = 4;
                    break;
                  case 'E':
                  case 'N':
                  case 'V':
                    digitValue = 5;
                    break;
                  case 'F':
                  case 'O':
                  case 'W':
                    digitValue = 6;
                    break;
                  case 'G':
                  case 'P':
                  case 'X':
                    digitValue = 7;
                    break;
                  case 'H':
                  case 'Y':
                    digitValue = 8;
                    break;
                  case 'R':
                  case 'Z':
                    digitValue = 9;
                    break;
                  default:
                    digitValue = Number(vin.charAt(i));
                    break;
                }
              } else {
                checkDigit = vin.charAt(i);
              }

              switch (i + 1) {
                case 1:
                case 11:
                  vinTotal += digitValue * 8;
                  break;
                case 2:
                case 12:
                  vinTotal += digitValue * 7;
                  break;
                case 3:
                case 13:
                  vinTotal += digitValue * 6;
                  break;
                case 4:
                case 14:
                  vinTotal += digitValue * 5;
                  break;
                case 5:
                case 15:
                  vinTotal += digitValue * 4;
                  break;
                case 6:
                case 16:
                  vinTotal += digitValue * 3;
                  break;
                case 7:
                case 17:
                  vinTotal += digitValue * 2;
                  break;
                case 8:
                  vinTotal += digitValue * 10;
                  break;
                case 9:
                  break;
                case 10:
                  vinTotal += digitValue * 9;
                  break;
              }
            }
            if (vinTotal % 11 == checkDigit || (vinTotal % 11 == 10 && checkDigit === 'X')) {
            } else {
              this.vinError = 'vinCheckDigitFailed';
            }
          }
        }),
        switchMap((vin: any) => {
          let vehicleQueryParams: VehicleQueryParameters = new VehicleQueryParameters({ vin: vin });
          return this.apiService.get<any[]>(`v/1/vehicles/vehicles`, { params: vehicleQueryParams }).pipe(
            mergeMap(vehicles => {
              return of({ vin, vehicles });
            })
          );
        }),
        switchMap(data => {
          if (data.vehicles.length > 0) {
            this.clearVehicleForm(['vin', 'rooftopId']);
            this.disableVehicleForm(['vin']);
            this.vehicleForm.get('vin').setErrors({ vehicleExists: true });
            return of(null);
          } else {
            return this.apiService.get<any[]>(`v/1/vehicles/vin-translate/${data.vin}/1/trims`).pipe(
              map(trims => {
                this.vehicleForm.get('vin').setErrors(null);
                if (trims.length > 0) {
                  this.trims = trims;
                  this.vehicleForm.get('trimLevel').setValidators(Validators.required);
                  this.vehicleForm.get('trimLevel').updateValueAndValidity({ emitEvent: false });
                  this.disableVehicleForm(['vin', 'trimLevel']);
                } else {
                  this.trims = [];
                  this.vehicleForm.get('trimLevel').patchValue(null, { emitEvent: false });
                  this.enableVehicleForm(['trimLevel']);
                }
                this.clearVehicleForm(['vin', 'rooftopId']);
              }),
              catchError(error => {
                if (error.statusCode == 422) {
                  this.trims = [];
                  this.vehicleForm.get('trimLevel').patchValue(null, { emitEvent: false });
                  this.enableVehicleForm(['trimLevel']);
                  this.clearVehicleForm(['vin', 'rooftopId']);
                }
                return of(error);
              })
            );
          }
        })
      )
      .subscribe();

    this.vehicleFormTrimLevelSub = this.vehicleForm['controls'].trimLevel.valueChanges
      .pipe(
        debounceTime(500),
        switchMap((value: any) => {
          return combineLatest(
            this.apiService.get<any[]>(
              `v/1/vehicles/vin-translate/${this.vehicleForm.get('vin').value}/1/trims/${value}`
            ),
            this.vehicleDropdowns$
          ).pipe(
            switchMap(vinTranslate => {
              this.bodyDropdowns$ = combineLatest(
                this.apiService.get<any[]>(`v/1/vehicles/vehicle-bodies`),
                this.vehicleForm.get('body').valueChanges.pipe(filter(value => value.length > 1))
              ).pipe(
                map(([bodies, value]) => {
                  return bodies.filter((body: any) => body.vehicleBody.toLowerCase().includes(value.toLowerCase()));
                })
              );
              return of(vinTranslate);
            }),
            switchMap(data => {
              this.enableVehicleForm();
              this.vehicleForm.patchValue({
                ...this.vehicleForm,
                year: data[0].year,
                make: data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                  ? data[1].makes.find((make: any) => make.description.toLowerCase() === data[0].make.toLowerCase())
                      .description
                  : null,
                model: data[0].model,
                engine: data[0].engine,
                subTypeId: data[0].subTypeId ? Number(data[0].subTypeId) : null,
                body: data[0].body,
                manufacturerId: data[0].manufacturer,
                regularWarrantyId:
                  data[0].manufacturer.toLowerCase() === 'gm'
                    ? data[0].make.toLowerCase() === 'cadillac'
                      ? 129
                      : 1
                    : null
              });

              return this.apiService
                .get<Status[]>(`v/1/vehicles/vehicle-statuses`, {
                  params: { code: 3, manufacturerId: data[0].manufacturer }
                })
                .pipe(
                  map(statuses => {
                    this.vehicleForm.patchValue({
                      ...this.vehicleForm,
                      statusCode: statuses[0].id,
                      subCategoryId: 2
                    });
                  }),
                  catchError(error => {
                    return of(error);
                  })
                );
            }),
            catchError(error => {
              return of(error);
            })
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.vehicleFormVinSub) {
      this.vehicleFormVinSub.unsubscribe();
    }
    if (this.vehicleFormTrimLevelSub) {
      this.vehicleFormTrimLevelSub.unsubscribe();
    }
    if (this.addressFormProvStateSub) {
      this.addressFormProvStateSub.unsubscribe();
    }
    if (this.vehicleFormChangesSub) {
      this.vehicleFormChangesSub.unsubscribe();
    }
  }

  disableVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).disable({ emitEvent: false });
        } else {
          this.vehicleForm.get(key).enable({ emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        this.vehicleForm.get(key).disable({ emitEvent: false });
      });
    }
  }

  clearVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).patchValue(null, { emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        this.vehicleForm.get(key).patchValue(null, { emitEvent: false });
      });
    }
  }

  enableVehicleForm(fieldsToSkip?: string[]) {
    if (fieldsToSkip && fieldsToSkip.length > 0) {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        if (!fieldsToSkip.includes(key)) {
          this.vehicleForm.get(key).enable({ emitEvent: false });
        } else {
          this.vehicleForm.get(key).disable({ emitEvent: false });
        }
      });
    } else {
      Object.keys(this.vehicleForm.getRawValue()).forEach(key => {
        this.vehicleForm.get(key).enable({ emitEvent: false });
      });
    }
  }

  setVehicleRequiredFields() {
    this.vehicleForm.get('vin').setValidators(Validators.required);
    this.vehicleForm.get('subTypeId').setValidators(Validators.required);
    this.vehicleForm.get('year').setValidators(Validators.required);
    this.vehicleForm.get('make').setValidators(Validators.required);
    this.vehicleForm.get('model').setValidators(Validators.required);
    this.vehicleForm.get('body').setValidators(Validators.required);
    this.vehicleForm.get('engine').setValidators(Validators.required);
    this.vehicleForm.get('prospectColourId').setValidators(Validators.required);
  }

  setVehicleType(subType: SubType) {
    this.vehicleForm.patchValue({ typeId: subType.typeId });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.vehicle) {
      this.vehicleForm = createFormGroupFromObject(changes.vehicle.currentValue);
      this.vehicleForm.markAllAsTouched();
      this.disableVehicleForm(['vin']);
      if (changes.vehicle.currentValue.vin != null) {
        this.vehicleForm.get('vin').setValue(changes.vehicle.currentValue.vin);
        this.apiService
          .get<any[]>(`v/1/vehicles/vin-translate/${changes.vehicle.currentValue.vin}/1/trims`)
          .pipe(
            map(trims => {
              this.trims = trims;
            }),
            catchError(error => {
              return of(error);
            }),
            take(1)
          )
          .subscribe();
      }
      this.setVehicleRequiredFields();
      this.vehicleForm.markAllAsTouched();
    }
  }
}
